import { GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import { WatchItemRow } from "../../../utils/preference/watchItemPayload";
import { token } from "../../components/FormKit/styled";

interface WatchItemTableColumnsProps {
  handleOnDelete: (value: WatchItemRow) => void;
}

const monoStyle = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  color: token.color.textMuted,
};

export const useWatchItemTableColumns = ({
  handleOnDelete,
}: WatchItemTableColumnsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateColumns: AllowedTableColumn[] = ["updatedAt", "createdAt"];

  const handleOnClickEdit = (value: WatchItemRow) => {
    navigate(routes.preferenceWatchItem.editById(value?.id || ""));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["edit"]}
          requiredPermissionGroup={["tag"]}
        >
          <RenderEditButton onClick={handleOnClickEdit} {...props} />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
    {
      field: "name",
      width: 280,
      headerName: `${t(messages.preference_watch_item_name())}`,
    },
    {
      field: "isCurated",
      width: 130,
      headerName: `${t(messages.preference_watch_item_curated())}`,
      renderCell: (props: GridRenderCellParams<WatchItemRow>) =>
        props.row.isCurated ? (
          <Tooltip
            title={t(messages.preference_watch_item_curated_helper())}
            arrow
          >
            <Chip
              size="small"
              label={t(messages.preference_watch_item_curated_yes())}
              sx={{
                height: 22,
                fontSize: 12,
                backgroundColor: token.color.accentSoft,
                color: "#7a5230",
                fontWeight: 600,
              }}
            />
          </Tooltip>
        ) : (
          <span style={{ color: token.color.textMuted, fontSize: 13 }}>
            {t(messages.preference_watch_item_curated_no())}
          </span>
        ),
    },
    {
      field: "normalizedName",
      width: 260,
      headerName: `${t(messages.preference_watch_item_normalized())}`,
      renderCell: (props: GridRenderCellParams<WatchItemRow>) => (
        <Tooltip
          title={t(messages.preference_watch_item_normalized_helper())}
          arrow
        >
          <span style={monoStyle}>{props.row.normalizedName}</span>
        </Tooltip>
      ),
    },
    ...dateColumns.map((value) => ({
      width: 200,
      field: value,
      headerName: `${t(messages[value]())}`,
      type: "dateTime",
      valueGetter: (val: GridRenderCellParams) =>
        val?.row?.[value] ? new Date(val.row[value]) : undefined,
    })),
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
    {
      field: "delete",
      width: 90,
      headerName: `${t(messages.Delete())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["delete"]}
          requiredPermissionGroup={["tag"]}
        >
          <RenderDeleteButton
            onClick={() => handleOnDelete(props.row as WatchItemRow)}
            {...props}
          />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
  ];

  return columns;
};
